/**
 * Наряды и путевые листы (ТХ-02) — экран для телефона: большие кнопки, одна колонка. Наряд выдаётся вручную
 * или из плана дня графика; путевой лист заполняет машинист или прораб, подписи ставятся по очереди. При
 * повторной отправке с тем же ключом портал возвращает тот же лист, без дублей.
 */
import { IDLE_REASONS, WAYBILL_STATUSES, type WaybillRow, type WorkOrderRow } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from '../schedule/LField';
import { dd, n1, num, useFleetRights, useUnits } from './shared';

const WB_TONE = { draft: 'neutral', operator: 'info', signed: 'success', cancelled: 'danger' } as const;

export function FleetWaybills({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const rights = useFleetRights();
  const [day, setDay] = useState(todayLocal());
  const canWork = rights.canWaybill(projectId);
  const units = useUnits(projectId);
  const orders = useQuery({
    queryKey: ['fleet', 'orders', projectId, day],
    queryFn: () => api<WorkOrderRow[]>('/fleet/work-orders', { query: { projectId, day } }),
  });
  const lists = useQuery({
    queryKey: ['fleet', 'waybills', projectId, day],
    queryFn: () => api<WaybillRow[]>('/fleet/waybills', { query: { projectId, day } }),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fleet'] });
  const fromSchedule = useMutation({
    mutationFn: () =>
      api<{ created: WorkOrderRow[]; skipped: unknown[] }>('/fleet/work-orders/from-schedule', {
        method: 'POST',
        body: { projectId, day },
      }),
    onSuccess: (r) => {
      toast(
        r.created.length
          ? `Выдано нарядов: ${r.created.length}${r.skipped.length ? `, без машины: ${r.skipped.length}` : ''}`
          : 'По плану дня новых нарядов нет',
      );
      void refresh();
    },
  });
  const cancelOrder = useMutation({
    mutationFn: (id: string) => api(`/fleet/work-orders/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => void refresh(),
  });
  const [order, setOrder] = useState<boolean>(false);
  const [wb, setWb] = useState<{ order?: WorkOrderRow; open?: WaybillRow } | null>(null);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-end">
        <LField label="День">
          <Input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value || todayLocal())}
            className="h-11"
          />
        </LField>
        {canWork ? (
          <>
            <Button size="lg" variant="primary" icon={<Plus className="size-4" />} onClick={() => setWb({})}>
              Путевой лист
            </Button>
            <Button size="lg" onClick={() => setOrder(true)}>
              Выдать наряд
            </Button>
            <Button
              size="lg"
              icon={<CalendarCheck className="size-4" />}
              loading={fromSchedule.isPending}
              onClick={() => fromSchedule.mutate()}
            >
              Наряды из плана дня
            </Button>
          </>
        ) : null}
      </div>
      {fromSchedule.isError ? <ErrorBox error={fromSchedule.error} /> : null}
      <Panel title="Наряды на день" meta={dd(day)}>
        {orders.isPending ? <Spinner /> : null}
        {orders.isError ? <ErrorBox error={orders.error} /> : null}
        {orders.data && !orders.data.length ? (
          <EmptyState title="Нарядов нет">
            Выдайте наряд на машину или создайте их из плана дня графика.
          </EmptyState>
        ) : null}
        <ul className="divide-y divide-line">
          {(orders.data ?? []).map((o) => (
            <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="min-w-0">
                <span className="block font-medium [overflow-wrap:anywhere]">
                  {o.machineName}
                  {o.regNumber ? ` · ${o.regNumber}` : ''}
                </span>
                <span className="block text-[12px] text-ink-2">
                  {o.activityName ?? 'работа не указана'}
                  {o.zone ? ` · ${o.zone}` : ''}
                  {o.operatorName ? ` · ${o.operatorName}` : ''} · план {n1(o.plannedHours)} ч
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <Badge
                  tone={o.status === 'closed' ? 'success' : o.status === 'cancelled' ? 'danger' : 'info'}
                >
                  {o.status === 'issued' ? 'Выдан' : o.status === 'closed' ? 'Закрыт' : 'Отменён'}
                </Badge>
                {canWork && o.status === 'issued' ? (
                  <>
                    <Button size="sm" variant="primary" onClick={() => setWb({ order: o })}>
                      Заполнить лист
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => cancelOrder.mutate(o.id)}>
                      Отменить
                    </Button>
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
      <Panel title="Путевые листы" meta={dd(day)}>
        {lists.isPending ? <Spinner /> : null}
        {lists.isError ? <ErrorBox error={lists.error} /> : null}
        {lists.data && !lists.data.length ? <EmptyState title="Листов за день нет" /> : null}
        <ul className="divide-y divide-line">
          {(lists.data ?? []).map((w) => (
            <li key={w.id}>
              <button
                type="button"
                onClick={() => setWb({ open: w })}
                className="flex min-h-14 w-full flex-wrap items-center justify-between gap-2 py-2 text-left hover:underline"
              >
                <span className="min-w-0">
                  <span className="block font-medium [overflow-wrap:anywhere]">
                    {w.number} · {w.machineName}
                  </span>
                  <span className="block text-[12px] text-ink-2">
                    {w.workHours != null ? `${n1(w.workHours)} ч` : 'часы не заполнены'}
                    {w.idleHours ? ` · простой ${n1(w.idleHours)} ч` : ''}
                    {w.fuelUsed != null ? ` · топливо ${n1(w.fuelUsed)} л` : ''}
                    {w.operatorName ? ` · ${w.operatorName}` : ''}
                  </span>
                </span>
                <span className="flex items-center gap-1">
                  {w.mismatch.length ? <Badge tone="warning">Расходится с телематикой</Badge> : null}
                  <Badge tone={WB_TONE[w.status]}>{WAYBILL_STATUSES[w.status]}</Badge>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      {order ? (
        <OrderDialog
          projectId={projectId}
          day={day}
          onClose={() => setOrder(false)}
          machines={(units.data ?? []).map((u) => ({ id: u.machineId, name: u.name, reg: u.regNumber }))}
          onSaved={() => {
            setOrder(false);
            void refresh();
          }}
        />
      ) : null}
      {wb ? (
        <WaybillDialog
          key={wb.open?.id ?? wb.order?.id ?? 'new'}
          projectId={projectId}
          day={day}
          order={wb.order}
          existing={wb.open}
          machines={(units.data ?? []).map((u) => ({
            id: u.machineId,
            name: u.name,
            reg: u.regNumber,
            meter: u.hourMeter,
          }))}
          onClose={() => setWb(null)}
          onChanged={() => void refresh()}
        />
      ) : null}
    </div>
  );
}

function OrderDialog({
  projectId,
  day,
  machines,
  onClose,
  onSaved,
}: {
  projectId: string;
  day: string;
  machines: Array<{ id: string; name: string; reg: string | null }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({ machineId: '', zone: '', hours: '8' });
  const save = useMutation({
    mutationFn: () =>
      api('/fleet/work-orders', {
        method: 'POST',
        body: {
          projectId,
          machineId: f.machineId,
          day,
          zone: f.zone.trim() || null,
          plannedHours: num(f.hours) ?? 8,
        },
      }),
    onSuccess: () => {
      toast('Наряд выдан');
      onSaved();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Наряд на технику"
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            disabled={!f.machineId}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Выдать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? <ErrorBox error={save.error} /> : null}
        <LField label="Машина">
          <Select value={f.machineId} onChange={(e) => setF((x) => ({ ...x, machineId: e.target.value }))}>
            <option value="">Выберите машину</option>
            {machines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.reg ? ` · ${m.reg}` : ''}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Захватка или участок">
          <Input value={f.zone} onChange={(e) => setF((x) => ({ ...x, zone: e.target.value }))} />
        </LField>
        <LField label="План, часов">
          <Input
            inputMode="decimal"
            value={f.hours}
            onChange={(e) => setF((x) => ({ ...x, hours: e.target.value }))}
          />
        </LField>
      </div>
    </Dialog>
  );
}

function WaybillDialog({
  projectId,
  day,
  order,
  existing,
  machines,
  onClose,
  onChanged,
}: {
  projectId: string;
  day: string;
  order?: WorkOrderRow;
  existing?: WaybillRow;
  machines: Array<{ id: string; name: string; reg: string | null; meter: number | null }>;
  onClose: () => void;
  onChanged: () => void;
}) {
  // Ключ повторной отправки создаётся один раз на открытие формы: двойное нажатие и повтор не плодят листы
  const [clientOpId] = useState(() => crypto.randomUUID());
  const [w, setW] = useState<WaybillRow | undefined>(existing);
  const editable = !w || w.actions.includes('edit');
  const [f, setF] = useState({
    machineId: existing?.machineId ?? order?.machineId ?? '',
    hourStart: existing?.hourStart != null ? String(existing.hourStart) : '',
    hourEnd: existing?.hourEnd != null ? String(existing.hourEnd) : '',
    odoStart: existing?.odoStart != null ? String(existing.odoStart) : '',
    odoEnd: existing?.odoEnd != null ? String(existing.odoEnd) : '',
    idleHours: existing ? String(existing.idleHours) : '0',
    idleReason: existing?.idleReason ?? '',
    fuelStart: existing?.fuelStart != null ? String(existing.fuelStart) : '',
    fuelEnd: existing?.fuelEnd != null ? String(existing.fuelEnd) : '',
    note: existing?.note ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const body = () => ({
    hourStart: num(f.hourStart),
    hourEnd: num(f.hourEnd),
    odoStart: num(f.odoStart),
    odoEnd: num(f.odoEnd),
    idleHours: num(f.idleHours) ?? 0,
    idleReason: (num(f.idleHours) ?? 0) > 0 ? f.idleReason || null : null,
    fuelStart: num(f.fuelStart),
    fuelEnd: num(f.fuelEnd),
    note: f.note.trim() || null,
  });
  const save = useMutation({
    mutationFn: () =>
      w
        ? api<WaybillRow>(`/fleet/waybills/${w.id}`, { method: 'PATCH', body: body() })
        : api<WaybillRow>('/fleet/waybills', {
            method: 'POST',
            body: {
              ...body(),
              clientOpId,
              projectId,
              machineId: f.machineId,
              day,
              workOrderId: order?.id ?? null,
            },
          }),
    onSuccess: (r) => {
      setW(r);
      toast('Путевой лист сохранён');
      onChanged();
    },
  });
  const sign = useMutation({
    mutationFn: (as: 'operator' | 'foreman') =>
      api<WaybillRow>(`/fleet/waybills/${w!.id}/sign`, { method: 'POST', body: { as } }),
    onSuccess: (r) => {
      setW(r);
      toast(r.status === 'signed' ? 'Подписан: часы ушли в график' : 'Подпись машиниста поставлена');
      onChanged();
    },
  });
  const cancel = useMutation({
    mutationFn: () => api(`/fleet/waybills/${w!.id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const machine = machines.find((m) => m.id === f.machineId);
  const inp = (label: string, k: keyof typeof f, hint?: string) => (
    <LField label={label} hint={hint}>
      <Input
        inputMode="decimal"
        className="h-11"
        disabled={!editable}
        value={f[k]}
        onChange={(e) => set(k, e.target.value)}
      />
    </LField>
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={w ? `Путевой лист ${w.number}` : 'Новый путевой лист'}
      description={w ? `${WAYBILL_STATUSES[w.status]} · ${dd(w.day)}` : dd(day)}
      footer={
        <>
          <Button onClick={onClose}>Закрыть</Button>
          {w?.actions.includes('cancel') ? (
            <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Отменить лист
            </Button>
          ) : null}
          {editable ? (
            <Button
              variant="primary"
              disabled={!w && !f.machineId}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Сохранить
            </Button>
          ) : null}
          {w?.actions.includes('sign_operator') ? (
            <Button variant="primary" loading={sign.isPending} onClick={() => sign.mutate('operator')}>
              Подписать как машинист
            </Button>
          ) : null}
          {w?.actions.includes('sign_foreman') ? (
            <Button variant="primary" loading={sign.isPending} onClick={() => sign.mutate('foreman')}>
              Подписать как прораб
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? <ErrorBox error={save.error} /> : null}
        {sign.isError ? <ErrorBox error={sign.error} /> : null}
        {cancel.isError ? <ErrorBox error={cancel.error} /> : null}
        {!w ? (
          <LField label="Машина">
            <Select
              className="h-11"
              value={f.machineId}
              onChange={(e) => {
                set('machineId', e.target.value);
                const m = machines.find((x) => x.id === e.target.value);
                if (m?.meter != null && !f.hourStart) set('hourStart', String(m.meter));
              }}
            >
              <option value="">Выберите машину</option>
              {machines.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.reg ? ` · ${m.reg}` : ''}
                </option>
              ))}
            </Select>
          </LField>
        ) : (
          <p className="text-[14px] font-medium">
            {w.machineName}
            {w.activityName ? ` · ${w.activityName}` : ''}
          </p>
        )}
        {machine?.meter != null && !w ? (
          <p className="text-[12px] text-ink-2">Моточасы машины сейчас: {n1(machine.meter)}</p>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          {inp('Моточасы, начало', 'hourStart')}
          {inp('Моточасы, конец', 'hourEnd')}
          {inp('Пробег, начало', 'odoStart')}
          {inp('Пробег, конец', 'odoEnd')}
          {inp('Простой, часов', 'idleHours')}
          <LField label="Причина простоя">
            <Select
              className="h-11"
              disabled={!editable}
              value={f.idleReason}
              onChange={(e) => set('idleReason', e.target.value)}
            >
              <option value="">—</option>
              {Object.entries(IDLE_REASONS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </LField>
          {inp('Топливо в баке, начало, л', 'fuelStart')}
          {inp('Топливо в баке, конец, л', 'fuelEnd')}
        </div>
        {w ? (
          <p className="text-[13px] text-ink-2">
            Отработано {n1(w.workHours)} ч · заправлено за день {n1(w.fuelFilled)} л · расход {n1(w.fuelUsed)}{' '}
            л{w.mismatch.length ? ' · расходится с телематикой' : ''}
          </p>
        ) : null}
        <LField label="Заметка">
          <Textarea disabled={!editable} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </LField>
      </div>
    </Dialog>
  );
}
